import React, { useState } from 'react';
import PageHeader from '../components/HeroSection';
import ContactSectionDark from '../components/ContactSectionDark';
import { Helmet } from 'react-helmet';
const FAQPage = () => {
  const [openItems, setOpenItems] = useState({ 0: true }); // First item open by default

  const toggleItem = (index) => {
    setOpenItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Font styles for headers
  const fontStyles = {
    sectionHeader: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 700,
      fontStyle: 'normal',
      lineHeight: '110%',
      letterSpacing: '0.04em',
      textTransform: 'uppercase'
    },
    question: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 600,
      lineHeight: '120%',
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color: '#000000'
    },
    answer: {
      fontFamily: 'Montserrat, sans-serif',
      fontWeight: 400,
      lineHeight: '156%',
      color: '#333333'
    }
  };

  const faqSections = [
    {
      title: "GENERAL SALON QUESTIONS",
      questions: [
        {
          question: "Where is Hair To Dye For located?",
          answer: "We're conveniently located in downtown Easton, PA at 200 Northampton St. You can find directions and parking info on our Contact page or Google Maps.",
        },
        {
          question: "Do I need an appointment, or do you take walk-ins?",
          answer: "Appointments are recommended to guarantee your preferred stylist and service time. Limited walk-ins may be available—please call ahead to check.",
        },
        {
          question: "How can I book an appointment?",
          answer: "Please call the salon at 610-923-3530. All color and extension appointments require a consultation before booking.",
        },
      ],
    },
    {
      title: "HAIR EXTENSION SERVICES",
      questions: [
        {
          question: "What types of hair extensions do we do?",
          answer: "We specialize in flat tip, butterfly, hand tied, and tape in extensions. We proudly carry Malia, Aqua and Remi Catchet.",
        },
        {
          question: "How long do hair extensions last?",
          answer: "With proper care and maintenance, extensions typically last 6—8 weeks before needing to be moved up. The hair itself can be reused for up to 6—12 months, depending on the method and your at-home care routine.",
        },
        {
          question: "Will hair extensions damage my natural hair?",
          answer: "When applied and maintained by trained professionals, extensions will not damage your natural hair. We use safe application methods and provide aftercare instructions to protect your hair.",
        },
        {
          question: "How much do hair extensions cost?",
          answer: "Pricing varies based on the extension method, length, and amount of hair needed. We provide a personalized quote during your free consultation.",
        },
        {
          question: "How long does an extension appointment take?",
          answer: "Initial installation typically takes 2—4 hours, depending on the method and amount of hair being applied.",
        },
      ],
    },
    {
      title: "COLORING & TREATMENTS",
      questions: [
        {
          question: "Can I color my hair extensions?",
          answer: "Extensions made from 100% human hair can be colored darker but we do not recommend bleaching them. It's always best to have color services done by our professional stylists.",
        },
        {
          question: "How often should I get my hair colored or highlighted?",
          answer: "Most Client schedule a touchup for roots every 4 to 6 weeks and highlight clients due 10 to 12 weeks balayage clients can go much longer.",
        },
        {
          question: "What treatments do you offer for damaged hair?",
          answer: "We carry three different conditioning treatment based on how much time you have available and your budget. We offer a B3 treatment a Redken treatment and also a Goldwell treatment. The prices range from $30-$100. They really make a huge impact on your hair.",
        },
      ],
    },
    {
      title: "BOOKING POLICIES",
      questions: [
        {
          question: "Do you require a deposit for appointments?",
          answer: "All new clients require a deposit for any service.",
        },
        {
          question: "What is your cancellation or refund policy?",
          answer: "We kindly ask for at least 48 hours' notice for cancellations. Deposits are non-refundable but may be applied to rescheduled appointments within a set timeframe.",
        },
      ],
    },
    {
      title: "AFTERCARE & MAINTENANCE",
      questions: [
        {
          question: "How should I care for my hair extensions?",
          answer: "Use sulfate-free, extension-safe shampoo and conditioner, avoid heavy oils near the bonds, and gently brush from the ends up. We'll provide a full aftercare guide at your appointment.",
        },
        {
          question: "Can I swim or go to the gym with extensions?",
          answer: "Yes, but we recommend braiding your hair and rinsing it immediately after swimming to prevent tangling or chlorine damage.",
        },
        {
          question: "What products do you recommend for at-home care?",
          answer: "We carry professional salon products specifically designed for color-treated hair and extensions to keep your hair healthy and vibrant between visits.",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
  <title>Frequently Asked Questions | Hair to Dye For, Easton PA</title>
  <meta name="description" content="Find answers about extensions, aftercare, pricing, booking, and more. Everything you need to know before your salon visit." />
  <meta property="og:title" content="FAQ | Hair Extensions & Salon Services – Easton" />
  <meta property="og:description" content="Get answers to the most common client questions on services, policies, and hair care tips." />
  <meta property="og:url" content="https://www.hairtodyeforeaston.com/faq" />
  <meta property="og:type" content="website" />
  <meta name="keywords" content="hair extension FAQ, salon questions Easton PA, hair care tips, extension aftercare" />
  <link rel="canonical" href="https://www.hairtodyeforeaston.com/faq" />
</Helmet>
      {/* Page Header with Hero */}
      <PageHeader pageTitle="FAQ" />

      {/* FAQ Content Section */}
      <section className="pt-12 pb-16 px-4 sm:px-6 lg:px-8 xl:pt-24 xl:pb-20">
        <div className="max-w-7xl mx-auto">
          <h1 className="sr-only">Hair Extension & Salon Service FAQs</h1>
          {/* Mobile/Tablet Layout - Single Column */}
          <div className="xl:hidden">
            {faqSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="mb-12 sm:mb-16">
                <h3 
                  className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-left"
                  style={fontStyles.sectionHeader}
                >
                  {section.title}
                </h3>
                
                <div className="space-y-0">
                  {section.questions.map((item, questionIndex) => {
                    const globalIndex = faqSections.slice(0, sectionIndex).reduce((acc, s) => acc + s.questions.length, 0) + questionIndex;
                    
                    return (
                      <div key={globalIndex} className="border-b border-gray-300 last:border-b-0">
                        <button
                          onClick={() => toggleItem(globalIndex)}
                          className="w-full flex justify-between items-start py-4 sm:py-5 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                        >
                          <h4 
                            className="pr-4 text-sm sm:text-base text-left flex-1"
                            style={fontStyles.question}
                          >
                            {item.question}
                          </h4>
                          <div className="flex-shrink-0 mt-1">
                            <svg
                              className={`w-4 h-4 transform transition-transform duration-200 ${
                                openItems[globalIndex] ? 'rotate-180' : ''
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </svg>
                          </div>
                        </button>
                        
                        <div
                          className={`overflow-hidden transition-all duration-300 ease-in-out ${
                            openItems[globalIndex] ? 'max-h-96 pb-4 sm:pb-5' : 'max-h-0'
                          }`}
                        >
                          <div className="pr-8">
                            <p 
                              className="text-sm sm:text-base text-left"
                              style={fontStyles.answer}
                            >
                              {item.answer}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Layout - Two Column with Aligned Headers */}
          <div className="hidden xl:block">
            {faqSections.map((section, sectionIndex) => {
              const questionStartIndex = faqSections.slice(0, sectionIndex).reduce((acc, s) => acc + s.questions.length, 0);
              
              return (
                <div key={sectionIndex} className="mb-16">
                  {/* Grid layout for each section */}
                  <div className="grid grid-cols-12 gap-16">
                    {/* Left Column - Section Header */}
                    <div className="col-span-4">
                      {/* Sticky header that aligns with first question */}
                      <div className="sticky top-24">
                        <h3 
                          className="text-3xl lg:text-4xl text-gray-900 text-left"
                          style={fontStyles.sectionHeader}
                        >
                          {section.title}
                        </h3>
                      </div>
                    </div>
                    
                    {/* Right Column - Questions for this section */}
                    <div className="col-span-8">
                      <div className="space-y-0">
                        {section.questions.map((item, questionIndex) => {
                          const globalIndex = questionStartIndex + questionIndex;
                          
                          return (
                            <div key={globalIndex} className="border-b border-gray-300 last:border-b-0">
                              <button
                                onClick={() => toggleItem(globalIndex)}
                                className="w-full flex justify-between items-start py-5 lg:py-6 text-left focus:outline-none hover:bg-gray-50 transition-colors"
                              >
                                <h4 
                                  className="pr-4 text-lg xl:text-xl text-left flex-1"
                                  style={fontStyles.question}
                                >
                                  {item.question}
                                </h4>
                                <div className="flex-shrink-0 mt-1">
                                  <svg
                                    className={`w-5 h-5 transform transition-transform duration-200 ${
                                      openItems[globalIndex] ? 'rotate-180' : ''
                                    }`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 9l-7 7-7-7"
                                    />
                                  </svg>
                                </div>
                              </button>
                              
                              <div
                                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                  openItems[globalIndex] ? 'max-h-96 pb-5 lg:pb-6' : 'max-h-0'
                                }`}
                              >
                                <div className="pr-12">
                                  <p 
                                    className="text-base lg:text-lg text-left"
                                    style={fontStyles.answer}
                                  >
                                    {item.answer}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section with Map */}
      <ContactSectionDark />
    </div>
  );
};

export default FAQPage;